import { Building03Icon, Layers01Icon, Tag01Icon, UserGroupIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

const MODE_ICONS = {
  company: Building03Icon,
  team: UserGroupIcon,
  brand: Tag01Icon,
  entity: Layers01Icon,
};

const RadioIndicator = ({ selected, saveState }) => (
  <span
    className={cn(
      'absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors duration-300',
      selected ? 'border-primary-5' : 'border-border-secondary',
    )}>
    <AnimatePresence initial={false}>
      {selected && (
        <motion.span
          key="dot"
          className="flex"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0, transition: { duration: 0.2, ease: 'easeOut' } }}
          transition={{ duration: 0.3, ease: 'easeOut' }}>
          <motion.span
            className="h-3 w-3 rounded-full bg-primary-5"
            animate={saveState === 'saving' ? { scale: [1, 0.6, 1] } : { scale: 1 }}
            transition={
              saveState === 'saving'
                ? { delay: 0.4, duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.3, ease: 'easeOut' }
            }
          />
        </motion.span>
      )}
    </AnimatePresence>
  </span>
);

export const BillingModeCards = ({ modes = [], value, onChange, disabled = false, saveState }) => {
  const { t } = useTranslation();

  return (
    <div
      role="radiogroup"
      aria-label={t('company.tabs.billing.modes.groupLabel')}
      aria-busy={saveState === 'saving'}
      className="grid grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] gap-3">
      <span className="sr-only" aria-live="polite">
        {saveState === 'saved' ? t('company.tabs.billing.modes.saved') : ''}
      </span>
      {modes
        .filter(mode => MODE_ICONS[mode])
        .map(mode => {
          const icon = MODE_ICONS[mode];
          const title = t(`company.tabs.billing.modes.${mode}.title`);
          const description = t(`company.tabs.billing.modes.${mode}.description`);
          const selected = value === mode;

          return (
            <label
              key={mode}
              className={cn(
                'relative m-0 flex cursor-pointer flex-col gap-2 rounded-xl border bg-white p-5 transition-colors duration-300 ease-out',
                selected
                  ? 'border-primary-5 bg-[linear-gradient(rgba(78,24,187,0.08),rgba(78,24,187,0.08))] ring-1 ring-inset ring-primary-5'
                  : 'border-border-primary hover:border-border-secondary hover:bg-surface-secondary',
                { 'pointer-events-none': disabled, 'opacity-60': disabled && !selected },
              )}>
              <input
                type="radio"
                name="billing_mode"
                value={mode}
                aria-label={title}
                checked={selected}
                disabled={disabled}
                onChange={() => onChange(mode)}
                className="sr-only"
              />
              <RadioIndicator selected={selected} saveState={saveState} />
              <HugeiconsIcon
                icon={icon}
                size={22}
                strokeWidth={1.75}
                className={selected ? 'text-primary-5' : 'text-text-secondary'}
              />
              <span className="mt-1 pr-8 text-2xl font-semibold text-text-primary">{title}</span>
              <span
                className={cn(
                  'text-xl font-normal leading-snug transition-colors duration-300',
                  selected ? 'text-text-secondary' : 'text-text-tertiary',
                )}>
                {description}
              </span>
            </label>
          );
        })}
    </div>
  );
};
